import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import colors from "../../styles/colors";
import { SlArrowDown } from "react-icons/sl";
import { useTranslation } from "react-i18next";

const HomeHeroSection = () => {
  const { t } = useTranslation();
  const images = [
    "/assets/images/home/HomeHeroSection/hintz1.webp",
    "/assets/images/home/HomeHeroSection/branzan1.webp",
    "/assets/images/home/HomeHeroSection/hintz2.webp",
    "/assets/images/home/HomeHeroSection/bukarest1.webp",
    "/assets/images/home/HomeHeroSection/hintz3.webp",
    "/assets/images/home/HomeHeroSection/karitas1.webp",
  ];

  // Alt tags for SEO
  const imageAltKeys = [
    "hero_hintz1",
    "hero_branzan1",
    "hero_hintz2",
    "hero_bukarest1",
    "hero_hintz3",
    "hero_karitas1",
  ];

  const [currentImage, setCurrentImage] = useState(0);
  const [isManual, setIsManual] = useState(false);

  const scrollDown = () => {
    window.scrollBy({ top: 700, left: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (isManual) return;

    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isManual, images.length]);

  const handleImageChange = (index: number) => {
    setCurrentImage(index);
    setIsManual(true);
    setTimeout(() => setIsManual(false), 5000);
  };

  return (
    <div
      className="relative w-screen h-[75vh] md:h-screen overflow-hidden"
      style={{ backgroundColor: colors.black }}
    >
      {/* background image */}
      <motion.div
        key={currentImage}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: "none" }}
      >
        <img
          src={images[currentImage]}
          alt={t(`image_alt.home_hero.${imageAltKeys[currentImage]}`)}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          style={{ pointerEvents: "none" }}
        />
      </motion.div>

      {/* Dots indicator */}
      <div className="absolute left-6 top-1/2 transform -translate-y-1/2 flex flex-col gap-4 z-10">
        {images.map((_, index) => (
          <motion.div
            key={index}
            className="rounded-full cursor-pointer transition-all"
            style={{
              width: "8px",
              height: "8px",
              backgroundColor:
                currentImage === index
                  ? colors.pantoneBrown
                  : colors.pastelCream,
            }}
            onClick={() => handleImageChange(index)}
            whileHover={{ scale: 1.5 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </div>

      {/* content (Hero text) */}
      <div className="absolute flex flex-col h-full w-full items-center justify-center bg-black bg-opacity-40 p-8 sm:p-12 md:p-16">
        <motion.p
          className="text-xl sm:text-3xl md:text-4xl font-serif text-center z-10 mt-64"
          style={{ color: colors.lightCream }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 1,
            delay: 0.8,
            type: "spring",
            stiffness: 80,
          }}
        >
          {t("homeHeroText")}
        </motion.p>
      </div>

      {/* Scroll down icon */}
      <div className="absolute bottom-6 sm:bottom-8 md:bottom-12 w-full flex justify-center">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <SlArrowDown
            className="text-3xl sm:text-4xl md:text-5xl text-white animate-bounce hover:text-gray-300 cursor-pointer"
            onClick={scrollDown}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default HomeHeroSection;
